import { InfoCircleFilled } from '@ant-design/icons';
import { Alert } from 'antd';
import dayjs from 'dayjs';
import React, { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import type {
  InfoRow,
  InfoSection,
} from 'app/components/commons/InfoBlock/InfoBlock';
import { InfoBlock } from 'app/components/commons/InfoBlock/InfoBlock';
import styles from 'app/components/commons/InfoBlock/InfoBlock.module.scss';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import type { BookingDetails } from 'app/typings/core/extranet/bookings/booking';
import {
  getExtraRows,
  getFreeIncludedPerNightRow,
  getIncludedPerOnceRows,
  getPaidIncludedPerNightRows,
  getRoomRows,
  mapPriceSubRows,
} from 'app/utils/core/bookingDetails/utils';

type Props = {
  bookingDetails: BookingDetails;
};

export const PaymentDetails = ({ bookingDetails }: Props) => {
  const { currencyFormatter } = useContext(CurrencyContext);
  const { data: bedCategories } = useBedCategories();

  const isMultiNightBooking =
    dayjs(bookingDetails.checkOut)
      .startOf('day')
      .diff(dayjs(bookingDetails.checkIn).startOf('day'), 'day') > 1;

  const freePerNightRow = getFreeIncludedPerNightRow(bookingDetails);
  const paidPerNightRows = getPaidIncludedPerNightRows(
    bookingDetails,
    currencyFormatter
  );
  const perOnceRows = getIncludedPerOnceRows(bookingDetails, currencyFormatter);
  const extraRows = getExtraRows(bookingDetails, currencyFormatter);

  const hasPerNightRows =
    isMultiNightBooking &&
    (freePerNightRow !== null || paidPerNightRows.length > 0);
  const hasPerOnceRows = perOnceRows.length > 0;

  const includedRows: InfoRow[] = [
    ...(hasPerNightRows
      ? [{ label: 'Included every night', variant: 'subheader' as const }]
      : []),
    ...(freePerNightRow
      ? [
          {
            label: freePerNightRow.label,
            value: currencyFormatter(freePerNightRow.price),
            bold: false,
          },
        ]
      : []),
    ...paidPerNightRows.map(({ label, price, subRows }) => ({
      label,
      value: currencyFormatter(price),
      bold: false,
      subRows: subRows
        ? mapPriceSubRows(subRows, currencyFormatter)
        : undefined,
    })),
    ...(hasPerOnceRows
      ? [{ label: 'Included once', variant: 'subheader' as const }]
      : []),
    ...perOnceRows.map(({ label, price, subRows }) => ({
      label,
      value: currencyFormatter(price),
      bold: false,
      subRows: subRows
        ? mapPriceSubRows(subRows, currencyFormatter)
        : undefined,
    })),
  ];

  const sections: InfoSection[] = [
    ...(bookingDetails.rooms.length > 0
      ? [
          {
            title: 'Room',
            rows: getRoomRows(bookingDetails, bedCategories ?? []).map(
              ({ label, price, subRows }) => ({
                label,
                value: currencyFormatter(price),
                bold: false,
                subRows: subRows
                  ? mapPriceSubRows(subRows, currencyFormatter)
                  : undefined,
              })
            ),
          },
        ]
      : []),
    {
      title: bookingDetails.pack.nameForHotel,
      rows: includedRows,
    },
    ...(extraRows.length > 0
      ? [
          {
            title: 'Extras',
            rows: extraRows.map(
              ({
                label,
                unitPrice,
                price,
                subRows,
                showUnitPrice,
                isOriginals,
              }) => ({
                label: showUnitPrice
                  ? `${label} (${
                      isOriginals ? 'Originals • ' : ''
                    }${currencyFormatter(unitPrice)})`
                  : label,
                value: currencyFormatter(price),
                bold: false,
                subRows: subRows
                  ? mapPriceSubRows(subRows, currencyFormatter)
                  : undefined,
              })
            ),
          },
        ]
      : []),
    {
      rows: [
        {
          label: 'Total paid incl. VAT',
          value: currencyFormatter(bookingDetails.payments[0].totalAmount ?? 0),
          bold: true,
          isTitle: true,
          isValueCentered: true,
        },
      ],
      ...(isMultiNightBooking && !bookingDetails.pack.isDaytime
        ? {
            info: (
              <Alert
                type="warning"
                message={
                  <span>
                    <strong>Only one Staycation welcome kit</strong> should be
                    handed out when guests arrive.
                  </span>
                }
                icon={<InfoCircleFilled />}
                showIcon
              />
            ),
            infoPosition: 'before' as const,
          }
        : {}),
    },
    {
      rows: [
        {
          label: 'Net commission amount excl.',
          value: currencyFormatter(bookingDetails.payments[0].hotelAmount ?? 0),
        },
        {
          label: 'No cancellations or refunds',
          value: '',
          variant: 'subrow',
        },
      ],
      info: (
        <Alert
          type="info"
          message="Please save an imprint of the guest's credit card"
          icon={<InfoCircleFilled />}
          className={styles.infoAlert}
          showIcon
        />
      ),
      infoPosition: 'after',
    },
  ];

  return <InfoBlock sections={sections} withBorder={false} />;
};
